import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import PincodeInput from '@/components/forms/PincodeInput';
import PassportOCRButton from './PassportOCRButton';
import { extractOcrName, extractOcrAddress, extractOcrPincode } from '@/utils/ocrFields';

const COUNTRY_CODES = [
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+82', country: 'South Korea', flag: '🇰🇷' },
  { code: '+39', country: 'Italy', flag: '🇮🇹' },
  { code: '+34', country: 'Spain', flag: '🇪🇸' },
  { code: '+7', country: 'Russia', flag: '🇷🇺' },
  { code: '+55', country: 'Brazil', flag: '🇧🇷' },
  { code: '+52', country: 'Mexico', flag: '🇲🇽' },
  { code: '+27', country: 'South Africa', flag: '🇿🇦' },
  { code: '+234', country: 'Nigeria', flag: '🇳🇬' },
  { code: '+62', country: 'Indonesia', flag: '🇮🇩' },
  { code: '+60', country: 'Malaysia', flag: '🇲🇾' },
  { code: '+66', country: 'Thailand', flag: '🇹🇭' },
  { code: '+84', country: 'Vietnam', flag: '🇻🇳' },
  { code: '+63', country: 'Philippines', flag: '🇵🇭' },
  { code: '+94', country: 'Sri Lanka', flag: '🇱🇰' },
  { code: '+977', country: 'Nepal', flag: '🇳🇵' },
  { code: '+880', country: 'Bangladesh', flag: '🇧🇩' },
  { code: '+92', country: 'Pakistan', flag: '🇵🇰' },
  { code: '+64', country: 'New Zealand', flag: '🇳🇿' },
  { code: '+353', country: 'Ireland', flag: '🇮🇪' },
  { code: '+31', country: 'Netherlands', flag: '🇳🇱' },
  { code: '+41', country: 'Switzerland', flag: '🇨🇭' },
  { code: '+43', country: 'Austria', flag: '🇦🇹' },
  { code: '+46', country: 'Sweden', flag: '🇸🇪' },
  { code: '+47', country: 'Norway', flag: '🇳🇴' },
  { code: '+45', country: 'Denmark', flag: '🇩🇰' },
  { code: '+358', country: 'Finland', flag: '🇫🇮' },
  { code: '+48', country: 'Poland', flag: '🇵🇱' },
  { code: '+90', country: 'Turkey', flag: '🇹🇷' },
  { code: '+20', country: 'Egypt', flag: '🇪🇬' },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '+974', country: 'Qatar', flag: '🇶🇦' },
  { code: '+968', country: 'Oman', flag: '🇴🇲' },
  { code: '+973', country: 'Bahrain', flag: '🇧🇭' },
  { code: '+965', country: 'Kuwait', flag: '🇰🇼' },
];

export const PersonalTab = ({ formData, handleChange, handleSelectChange }) => {
  // Map OCR-extracted passport JSON → form fields. Only sets fields that
  // come back populated AND that the user hasn't already filled, so manual
  // edits made before the scan aren't overwritten.
  const handlePassportExtract = (extracted) => {
    const setIfEmpty = (name, value) => {
      if (!value) return;
      const current = (formData?.[name] || '').toString().trim();
      if (current) return;
      handleChange({ target: { name, value } });
    };
    const setSelectIfEmpty = (name, value) => {
      if (!value) return;
      const current = (formData?.[name] || '').toString().trim();
      if (current) return;
      handleSelectChange(name, value);
    };

    setIfEmpty('fullName', extractOcrName(extracted));
    // Phase 16.2.1 — also populate the split-name inputs so the form is
    // ready for both backends (legacy fullName-only and new givenName +
    // surname).
    setIfEmpty('givenName', extracted.givenName);
    setIfEmpty('surname', extracted.surname);
    setIfEmpty('dateOfBirth', extracted.dateOfBirth);
    setIfEmpty('passportNumber', extracted.passportNumber);
    setIfEmpty('passportIssueDate', extracted.passportIssueDate);
    setIfEmpty('passportExpiryDate', extracted.passportExpiryDate);
    setIfEmpty('passportIssuedAt', extracted.passportIssuedAt);
    setIfEmpty('nationality', extracted.nationality);
    setIfEmpty('placeOfBirth', extracted.placeOfBirth);
    setIfEmpty('fatherName', extracted.fatherName);
    setIfEmpty('motherName', extracted.motherName);
    setIfEmpty('spouseName', extracted.spouseName);
    setIfEmpty('address', extractOcrAddress(extracted));
    setIfEmpty('pincode', extractOcrPincode(extracted));

    // Gender comes through as "M"/"F"/"Male"/"Female" — normalise
    if (extracted.gender) {
      const g = extracted.gender.trim().toLowerCase();
      const normalised = g.startsWith('m') ? 'Male' : g.startsWith('f') ? 'Female' : '';
      if (normalised) setSelectIfEmpty('gender', normalised);
    }

    // If passport says nationality "Indian"/"INDIA" and traveller has no
    // dial code, default to +91. Doesn't override an existing selection.
    if (extracted.nationality && /ind/i.test(extracted.nationality)) {
      setSelectIfEmpty('countryCode', '+91');
    }
  };

  return (
    <Card className="p-6 sm:p-10 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <h2 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900">Personal Information</h2>
        <div className="flex flex-wrap items-center gap-2">
          <PassportOCRButton
            onExtract={handlePassportExtract}
            docType="passport"
            label="Scan Passport &amp; Auto-fill"
            variant="default"
            className="bg-teal-600 hover:bg-teal-700 text-white"
            testId="scan-passport-personal-btn"
          />
          <PassportOCRButton
            onExtract={handlePassportExtract}
            docType="aadhar"
            label="Scan Aadhaar &amp; Auto-fill"
            variant="outline"
            className="border-teal-300 text-teal-700 hover:bg-teal-50"
            testId="scan-aadhaar-personal-btn"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-3 block">Given Name *</Label>
          <Input
            name="givenName"
            data-testid="input-given-name"
            value={formData.givenName || ''}
            onChange={(e) => {
              handleChange(e);
              // Keep fullName in sync as the operator types (live join with
              // surname). The form already submits fullName to the API; this
              // also keeps any legacy code reading formData.fullName happy.
              const next = `${e.target.value || ''} ${formData.surname || ''}`.trim();
              handleChange({ target: { name: 'fullName', value: next } });
            }}
            required
            className="h-12 rounded-xl"
            placeholder="As on passport (First + Middle)"
          />
        </div>

        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-3 block">Surname</Label>
          <Input
            name="surname"
            data-testid="input-surname"
            value={formData.surname || ''}
            onChange={(e) => {
              handleChange(e);
              const next = `${formData.givenName || ''} ${e.target.value || ''}`.trim();
              handleChange({ target: { name: 'fullName', value: next } });
            }}
            className="h-12 rounded-xl"
            placeholder="As on passport"
          />
        </div>

        <div className="md:col-span-2">
          <Label className="text-sm font-semibold text-slate-700 mb-3 block">
            Full Name (auto)
            <span className="ml-2 text-[10px] text-slate-400 font-normal normal-case">derived from Given Name + Surname</span>
          </Label>
          <Input
            name="fullName"
            data-testid="input-fullname"
            value={formData.fullName || ''}
            onChange={handleChange}
            className="h-11 rounded-xl bg-slate-50"
            placeholder="Auto-populated"
          />
        </div>

        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-3 block">Email *</Label>
          <Input name="email" data-testid="input-email" type="email" value={formData.email} onChange={handleChange} required className="h-12 rounded-xl" placeholder="email@example.com" />
        </div>
        
        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-3 block">Phone *</Label>
          <div className="flex gap-2">
            <Select value={formData.countryCode} onValueChange={(v) => handleSelectChange('countryCode', v)}>
              <SelectTrigger data-testid="select-country-code" className="w-28 sm:w-32 h-12 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNTRY_CODES.map(c => (
                  <SelectItem key={c.code} value={c.code}>{c.flag} {c.code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input name="phone" data-testid="input-phone" value={formData.phone} onChange={handleChange} required placeholder="XXXXXXXXXX" className="flex-1 h-12 rounded-xl" />
          </div>
        </div>
        
        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-3 block">Gender</Label>
          <Select value={formData.gender} onValueChange={(v) => handleSelectChange('gender', v)}>
            <SelectTrigger data-testid="select-gender" className="h-12 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Male">Male</SelectItem>
              <SelectItem value="Female">Female</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-3 block">Nationality</Label>
          <Input name="nationality" data-testid="input-nationality" value={formData.nationality || ''} onChange={handleChange} className="h-12 rounded-xl" placeholder="e.g. Indian" />
        </div>
        
        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-3 block">Marital Status</Label>
          <Select value={formData.maritalStatus} onValueChange={(v) => handleSelectChange('maritalStatus', v)}>
            <SelectTrigger data-testid="select-marital-status" className="h-12 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Single">Single</SelectItem>
              <SelectItem value="Married">Married</SelectItem>
              <SelectItem value="Divorced">Divorced</SelectItem>
              <SelectItem value="Widowed">Widowed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-3 block">Date of Birth</Label>
          <Input name="dateOfBirth" data-testid="input-dob" type="date" value={formData.dateOfBirth} onChange={handleChange} className="h-12 rounded-xl" />
        </div>
        
        {formData.maritalStatus === 'Married' && (
          <>
            <div className="animate-fade-in">
              <Label className="text-sm font-semibold text-slate-700 mb-3 block">Marriage Anniversary</Label>
              <Input name="marriageAnniversary" data-testid="input-anniversary" type="date" value={formData.marriageAnniversary} onChange={handleChange} className="h-12 rounded-xl" />
            </div>
            <div className="animate-fade-in">
              <Label className="text-sm font-semibold text-slate-700 mb-3 block">Number of Kids</Label>
              <Input name="numberOfKids" data-testid="input-kids" type="number" min="0" value={formData.numberOfKids} onChange={handleChange} className="h-12 rounded-xl" />
            </div>
          </>
        )}
        
        <div className="md:col-span-2">
          <Label className="text-sm font-semibold text-slate-700 mb-3 block">Address</Label>
          <Input name="address" data-testid="input-address" value={formData.address} onChange={handleChange} className="h-12 rounded-xl" placeholder="Enter full address" />
        </div>

        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-3 block">PIN Code</Label>
          <PincodeInput
            value={formData.pincode || ''}
            onChange={(v) => handleChange({ target: { name: 'pincode', value: v } })}
            onResolve={(r) => {
              if (r.city) handleChange({ target: { name: 'city', value: r.city } });
              if (r.state) handleChange({ target: { name: 'state', value: r.state } });
              if (r.country && !formData.country) handleChange({ target: { name: 'country', value: r.country } });
            }}
            testId="input-traveller-pincode"
          />
        </div>
        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-3 block">City</Label>
          <Input name="city" data-testid="input-traveller-city" value={formData.city || ''} onChange={handleChange} className="h-12 rounded-xl" placeholder="Auto-filled from PIN" />
        </div>
        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-3 block">State</Label>
          <Input name="state" data-testid="input-traveller-state" value={formData.state || ''} onChange={handleChange} className="h-12 rounded-xl" placeholder="Auto-filled from PIN" />
        </div>

        {/* Phase 17.2 #8 — corporate / GST claim fields. Always editable;
            hidden from the view-mode rendering until populated. The same
            companyGstNumber surfaces as an auto-fill option in the invoice
            create dialog. */}
        <div className="md:col-span-2 pt-4 mt-2 border-t border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-semibold text-slate-800">Corporate / GST details</Label>
            <span className="text-[10px] text-slate-400 font-normal">Optional — fill for corporate clients</span>
          </div>
        </div>
        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-3 block">Company Name</Label>
          <Input
            name="companyName"
            data-testid="input-company-name"
            value={formData.companyName || ''}
            onChange={handleChange}
            className="h-12 rounded-xl"
            placeholder="ACME Travels Pvt Ltd"
          />
        </div>
        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-3 block">Company GST Number</Label>
          <Input
            name="companyGstNumber"
            data-testid="input-company-gst"
            value={formData.companyGstNumber || ''}
            onChange={(e) => handleChange({ target: { name: 'companyGstNumber', value: (e.target.value || '').toUpperCase() } })}
            className="h-12 rounded-xl font-mono"
            placeholder="22AAAAA0000A1Z5"
            maxLength={15}
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-sm font-semibold text-slate-700 mb-3 block">Company Address</Label>
          <Input
            name="companyAddress"
            data-testid="input-company-address"
            value={formData.companyAddress || ''}
            onChange={handleChange}
            className="h-12 rounded-xl"
            placeholder="Registered office address as on GST certificate"
          />
        </div>
      </div>
    </Card>
  );
};
